import { useState } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { useAuth } from "@/auth/useAuth";
import { dailyApi } from "@/features/daily/api/daily.api";
import { useDailyQueryState } from "@/features/daily/hooks/use-daily-query-state";
import { useDailyFilters } from "@/features/daily/hooks/use-daily-filters";
import { DailyResourceStatus, useDailyResource } from "@/features/daily/hooks/use-daily-resource";
import { DailyStateBlock, DailySectionState } from "@/features/daily/components/DailyStateBlock";
import { DailyHeaderMeta } from "@/features/daily/components/DailyHeaderMeta";
import { SkipLink } from "@/features/daily/components/SkipLink";
import { TrendCard } from "@/features/daily/components/executive/ExecutivePanels";
import { CurrencySelector } from "@/features/daily/components/executive/CurrencySelector";
import {
    ActionsList,
    GrowthSummary,
    KpiGroups,
    RisksList,
} from "@/features/daily/components/executive/LegacyExecutive";
import {
    executiveSummaryViewModel,
    isExecutiveSummaryDto,
} from "@/features/daily/components/executive/executive-summary-view-model";
import { isExecutiveSeriesDto, seriesViewModel } from "@/features/daily/components/executive/series-view-model";
import { commandViewModel, dailyFormat } from "@/features/daily/components/command/command-view-model";
import "@/features/daily/styles/daily.css";

const BODY_ID = "daily-executive-main";
const POLL_MS = 120_000;

const SECTION_STATE: Record<DailyResourceStatus, DailySectionState> = {
    idle: "loading",
    loading: "loading",
    ok: "ok",
    partial: "partial",
    stale: "stale",
    error: "error",
    forbidden: "forbidden",
};

/**
 * D111: the legacy Executive overview, rebuilt on Daily's definitions. The KPI strip,
 * the growth table, the risks and the actions come from B8 (`/executive/summary`);
 * the two trend cards from B1 (`/executive/series`). Nothing here computes a figure.
 */
export function ExecutivePage() {
    const { state: authState } = useAuth();
    const { query, set } = useDailyQueryState();
    const [currencyKey, setCurrencyKey] = useState<string | null>(null);

    const viewerId = authState.user === null ? "" : authState.user.id;
    // D105: the app's bar drives the range. Executive's live default is month to date;
    // «Last full month» and «Custom date» arrive as `custom` bounds in the URL.
    const { filters, onFiltersChange } = useDailyFilters(query, set, "month_to_date");
    const period = query.windowPreset === "custom" && query.windowFrom !== null && query.windowTo !== null
        ? "custom"
        : "month_to_date";

    // L354/FR-009: the period travels as a preset, so the server decides where "this
    // month" currently ends. Only a custom range carries bounds, picked by a person.
    const params = {
        window: period,
        from: period === "custom" ? query.windowFrom ?? "" : "",
        to: period === "custom" ? query.windowTo ?? "" : "",
        scope: query.scope.join(","),
        types: query.types.join(","),
    };

    const summaryResource = useDailyResource(
        { resource: "executive-summary", scope: params.scope, query: params },
        (signal) => dailyApi.getExecutiveSummary(params, { signal }),
        { identity: viewerId, isData: isExecutiveSummaryDto, pollMs: POLL_MS },
    );

    const seriesResource = useDailyResource(
        { resource: "executive-series", scope: params.scope, query: params },
        (signal) => dailyApi.getExecutiveSeries(params, { signal }),
        { identity: viewerId, isData: isExecutiveSeriesDto, pollMs: POLL_MS },
    );

    const summary = summaryResource.data;
    const series = seriesResource.data;
    const currencies = summary === null ? [] : executiveSummaryViewModel.currencies(summary);
    const selectedCurrency = currencyKey ?? (summary === null ? "" : executiveSummaryViewModel.topCurrency(summary));
    const currencyValue = selectedCurrency === "" ? null : selectedCurrency;
    const volumeVm = series === null ? null : seriesViewModel.currency(series, selectedCurrency, "volume");
    const revenueVm = series === null ? null : seriesViewModel.currency(series, selectedCurrency, "revenue");

    const busy = summaryResource.status === "loading" || summaryResource.status === "idle";
    // D143: what the footer said — when the page asked, when the evidence was produced,
    // its quality and policy, and the currency rule — lives in the chart titles' (i).
    const sourceNotes = [
        `Consulta realizada: ${dailyFormat.stamp(summaryResource.meta.fetchedAt)}`,
        `Evidencia de: ${dailyFormat.stamp(summaryResource.meta.sourceAsOf)}`,
        ...(summaryResource.meta.stale ? ["La evidencia está desactualizada"] : []),
        ...(summary === null ? [] : [`Evidencia: ${commandViewModel.qualityLabel(summary.envelope.quality)} · ${dailyFormat.policy(summary.envelope.policy.policyVersion, summary.envelope.policy.policyFingerprint)}`]),
        "Cada moneda por separado · sin conversión entre monedas",
    ];
    // FR-024: a background refresh keeps the figures; a failed one keeps them AND
    // says the source failed.
    const sectionState: DailySectionState = summary !== null && busy ? "ok" : SECTION_STATE[summaryResource.status];

    return (
        <Shell title="Executive" subtitle="Resumen ejecutivo">
            <div className="daily-page daily-page--exec">
                <SkipLink targetId={BODY_ID} />
                <PageHeader
                    title="Resumen ejecutivo"
                    lede="Indicadores, tendencias, riesgos y acciones del periodo seleccionado."
                    right={(
                        <DailyHeaderMeta
                            chips={null}
                            filters={filters}
                            onFiltersChange={onFiltersChange}
                            busy={busy}
                            onRefresh={() => { summaryResource.refresh(); seriesResource.refresh(); }}
                        />
                    )}
                />

                <div className="daily-body" id={BODY_ID} tabIndex={-1}>
                    <DailyStateBlock
                        state={sectionState}
                        label="Resumen ejecutivo"
                        detail={summaryResource.error === null ? undefined : summaryResource.error.message}
                        onRetry={summaryResource.refresh}
                    >
                        {summary === null ? null : (
                            <>
                                <KpiGroups groups={executiveSummaryViewModel.groups(summary, selectedCurrency)} />

                                {volumeVm === null || revenueVm === null ? (
                                    seriesResource.status === "error" || seriesResource.status === "forbidden" ? (
                                        <p className="daily-small daily-muted" role="status">No se pudieron cargar las tendencias.</p>
                                    ) : null
                                ) : (
                                    <div className="daily-trend-row">
                                        <TrendCard
                                            title="Tendencia de ingresos"
                                            measureLabel="Ingresos"
                                            tone="green"
                                            vm={revenueVm}
                                            currencyValue={currencyValue}
                                            control={<CurrencySelector currencies={currencies} currencyKey={selectedCurrency} onCurrency={setCurrencyKey} />}
                                            notes={sourceNotes}
                                        />
                                        <TrendCard title="Tendencia de volumen" measureLabel="Volumen" tone="blue" vm={volumeVm} currencyValue={currencyValue} notes={sourceNotes} />
                                    </div>
                                )}

                                <div className="daily-lower-grid">
                                    <GrowthSummary rows={executiveSummaryViewModel.growth(summary, selectedCurrency)} />
                                    <RisksList risks={executiveSummaryViewModel.risks(summary)} />
                                    <ActionsList actions={executiveSummaryViewModel.actions(summary)} />
                                </div>

                            </>
                        )}
                    </DailyStateBlock>

                </div>
            </div>
        </Shell>
    );
}
